import React from 'react';
import { Presentation, Sparkles, Play, Keyboard, MousePointerClick } from 'lucide-react';
import { StartTourButton } from '../components/PlatformTour';
import { TOUR_STEPS } from '../lib/tourSteps';

// Group steps by chapter for the outline view.
function groupByChapter(steps) {
  const groups = [];
  let last = null;
  steps.forEach((s, i) => {
    if (!last || last.chapter !== s.chapter) {
      last = { chapter: s.chapter, items: [] };
      groups.push(last);
    }
    last.items.push({ ...s, idx: i });
  });
  return groups;
}

export default function PresentPlatform() {
  const groups = groupByChapter(TOUR_STEPS);
  const totalSteps = TOUR_STEPS.length;
  const estMinutes = Math.max(30, Math.round(totalSteps * 1.5));

  return (
    <div className="p-6 md:p-10 max-w-[1200px] mx-auto" data-testid="present-platform">
      {/* Hero */}
      <div className="mb-10 flex items-start gap-4 flex-wrap">
        <div className="w-14 h-14 rounded-2xl bg-[#679286]/10 grid place-items-center shrink-0">
          <Presentation size={26} className="text-[#679286]"/>
        </div>
        <div className="flex-1 min-w-[300px]">
          <div className="text-[11px] uppercase tracking-widest text-[#679286] mb-1">Interactive tutorial</div>
          <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-3">
            Present the Platform
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl leading-relaxed">
            A guided tour that walks you through every module of Iris Realty — Portfolio,
            AI Deal Analyzer, Market Intelligence, Auctions, Ops, and Admin — using the
            <span className="font-semibold text-slate-900"> real live app</span>.
            Highlighted elements + presenter notes + auto page-navigation.
          </p>
        </div>
      </div>

      {/* Big start button */}
      <div className="bg-gradient-to-br from-[#679286]/10 to-white border border-[#679286]/30 rounded-2xl p-8 mb-8 flex items-center justify-between gap-6 flex-wrap">
        <div>
          <div className="text-2xl font-serif text-slate-900 mb-1">Ready when you are.</div>
          <div className="text-sm text-slate-600">
            {totalSteps} stops · ~{estMinutes} min · use <kbd className="px-1.5 py-0.5 rounded bg-white border text-[10px]">→</kbd> to advance, <kbd className="px-1.5 py-0.5 rounded bg-white border text-[10px]">Esc</kbd> to exit
          </div>
        </div>
        <StartTourButton />
      </div>

      {/* Instructions */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <InstructionCard icon={MousePointerClick} title="Live elements">
          The tour spotlights real UI. Interact freely — click into a listing, then hit Next to continue.
        </InstructionCard>
        <InstructionCard icon={Keyboard} title="Keyboard driven">
          <kbd className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px]">→</kbd> next ·{' '}
          <kbd className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px]">←</kbd> back ·{' '}
          <kbd className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px]">Esc</kbd> exit
        </InstructionCard>
        <InstructionCard icon={Sparkles} title="Presenter tip">
          Switch to the <span className="font-semibold">Demo</span> workspace before starting if the audience should not see real deals.
        </InstructionCard>
      </div>

      {/* Chapter outline */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3 font-semibold">
          Tour outline
        </div>
        <div className="space-y-3">
          {groups.map((g, gi) => (
            <div key={gi} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="font-serif text-lg text-slate-900">
                  {gi + 1}. {g.chapter}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">
                  {g.items.length} stop{g.items.length !== 1 ? 's' : ''}
                </div>
              </div>
              <ol className="space-y-1.5 pl-3">
                {g.items.map((step) => (
                  <li key={step.idx} className="text-sm text-slate-700 flex gap-2">
                    <span className="text-[#679286] font-mono text-[10px] mt-0.5 tabular-nums">
                      {String(step.idx + 1).padStart(2, '0')}
                    </span>
                    <span className="flex-1">
                      <span className="font-medium text-slate-900">{step.title}.</span>{' '}
                      <span className="text-slate-500">{step.body}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="mt-10 flex justify-center">
        <StartTourButton className="text-base px-6 py-4" />
      </div>
    </div>
  );
}

function InstructionCard({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="w-8 h-8 rounded-lg bg-[#679286]/10 grid place-items-center mb-2">
        <Icon size={16} className="text-[#679286]"/>
      </div>
      <div className="font-semibold text-slate-900 text-sm mb-1">{title}</div>
      <div className="text-xs text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
}
